import React from 'react';
import {useDispatch, useSelector} from "react-redux";
import {Box, Grid, useMediaQuery, useTheme} from "@material-ui/core";
import StopoverLeg from "../../../../Forms/Stopover/StopoverLeg/StopoverLeg";
import {t} from 'ttag';
import {
    BookStopoverSubmitButtonConnected,
    BookStopoverAlertConnected,
    PassengersCountSelectorConnected,
    StopoverAzoresImage,
    StopoverCloseButton,
    StopoverGridImages,
    StopoverPageTitle,
    BookStopoverWithExperiencesConnected
} from "../../../../index";
import {SW_FLIGHT_TYPES} from "../../../../../store/types";
import {
    setSwBookStopoverInboundState,
    setSwBookStopoverOutboundState,
    setSwBookStopoverState,
    setSwState
} from "../../../../../store/reducers/search-widget";
import {COMPONENTS, STOPOVER_REFS} from "../../common/book-form-input-refs";

const BookStopoverConnected = () => {

    const {breakpoints} = useTheme();
    const isMobile = useMediaQuery(breakpoints.down('xs'));
    const isTablet = useMediaQuery(breakpoints.down('md'));

    const focusedComponent = useSelector(state => state.sw.focusedComponent);
    const flightType = useSelector(state => state.sw.book.flightType);
    const stopover = useSelector(state => state.sw.book.stopover);
    const origin = useSelector(state => state.sw.book.origin);
    const destination = useSelector(state => state.sw.book.destination);
    const dispatch = useDispatch();
    const {availability, outbound, inbound, loading} = stopover;

    const isRoundTrip = flightType === SW_FLIGHT_TYPES.RETURN;
    const {outbound: aOutbound = null, inbound: aInbound = null} = availability || {};

    const outboundLegDisabled = Boolean((!aOutbound?.PDL.length && !aOutbound?.TER.length) || loading);
    const inboundLegDisabled = Boolean((!isRoundTrip || (isRoundTrip && !aInbound?.PDL.length && !aInbound?.TER.length)) || loading);

    return (
        <Box display={'flex'}
             position={'relative'}
             alignItems={'center'}
             justifyContent={'center'}
             bgcolor={'white'}
             color={'primary.main'}
             borderRadius={'5px'}
             marginTop={2}
             marginLeft={isMobile ? -3 : 0}
             marginRight={isMobile ? -3 : 0}
             p={4}
        >
            <StopoverCloseButton
                title={t`Skip Stopover`}
                style={{position: 'absolute', top: '-19px', right: '30px'}}
                onClick={() => dispatch(setSwBookStopoverState({open: false}))}
                aria-label={'Close Stopover'}
            />
            <Grid container spacing={4}>
                <Grid xs={12} md={12} lg={6} item>
                    <Grid container spacing={4}>
                        <Grid xs={12} item>
                            <StopoverPageTitle title={t`Add an AZORES STOPOVER to your trip`}/>
                        </Grid>
                        {isTablet ? (
                            <Grid xs={12} item>
                                <StopoverGridImages/>
                            </Grid>
                        ) : (
                            <Grid xs={12} item>
                                <StopoverAzoresImage
                                    title={`Azores`}
                                    origin={origin?.name}
                                    destination={destination?.name}
                                />
                            </Grid>
                        )}
                        <Grid xs={12} item>
                            <StopoverLeg
                                IslandSelectProps={{
                                    label: t`ON OUTGOING`,
                                    placeholder: t`Select the island`,
                                    value: outbound?.selectedAirport || '',
                                    disabledIslands: aOutbound ? Object.entries(aOutbound).reduce((a, [k, v]) => !v?.length ? [...a, k] : a, []) : [],
                                    onFocus: () => dispatch(setSwState({focusedComponent: COMPONENTS.STOPOVER_OUTBOUND_LEG_ISLAND_SELECTOR_REF})),
                                    onBlur: () => focusedComponent === COMPONENTS.STOPOVER_OUTBOUND_LEG_ISLAND_SELECTOR_REF && dispatch(setSwState({focusedComponent: COMPONENTS.NONE})),
                                    onSelect: selectedAirport => {
                                        dispatch(setSwBookStopoverOutboundState({selectedAirport}));
                                        dispatch(setSwState({focusedComponent: COMPONENTS.STOPOVER_OUTBOUND_LEG_NUMBER_OF_NIGHTS_SELECTOR_REF}));
                                    },
                                    disabled: outboundLegDisabled || !outbound?.enabled,
                                    open: focusedComponent === COMPONENTS.STOPOVER_OUTBOUND_LEG_ISLAND_SELECTOR_REF,
                                    containerRef: STOPOVER_REFS.STOPOVER_OUTBOUND_LEG_ISLAND_SELECTOR_REF,
                                    loading: loading,
                                }}
                                SwitchProps={{
                                    checked: Boolean(outbound?.enabled),
                                    onChange: (e, enabled) => dispatch(setSwBookStopoverOutboundState({enabled})),
                                    disabledAppearance: !outbound?.enabled,
                                    disabled: outboundLegDisabled,
                                    ariaLabel: 'Select Outgoing Island',
                                }}
                                NightsSelectProps={{
                                    label: t`DURATION`,
                                    placeholder: t`Nights`,
                                    value: outbound?.numberOfNights ? (outbound.numberOfNights === 1 ? t`${outbound.numberOfNights} night` : t`${outbound.numberOfNights} nights`) : '',
                                    availableNights: outbound?.selectedAirport ? aOutbound[outbound.selectedAirport] : [],
                                    onFocus: () => dispatch(setSwState({focusedComponent: COMPONENTS.STOPOVER_OUTBOUND_LEG_NUMBER_OF_NIGHTS_SELECTOR_REF})),
                                    onBlur: () => focusedComponent === COMPONENTS.STOPOVER_OUTBOUND_LEG_NUMBER_OF_NIGHTS_SELECTOR_REF && dispatch(setSwState({focusedComponent: COMPONENTS.NONE})),
                                    onSelect: availableNight => {
                                        dispatch(setSwBookStopoverOutboundState({numberOfNights: availableNight?.numberOfNights, departureDate: availableNight?.date}));
                                        dispatch(setSwState({focusedComponent: (!inboundLegDisabled && inbound.enabled) ? COMPONENTS.STOPOVER_INBOUND_LEG_ISLAND_SELECTOR_REF : COMPONENTS.NONE}));
                                    },
                                    disabled: outboundLegDisabled || !outbound?.enabled,
                                    open: focusedComponent === COMPONENTS.STOPOVER_OUTBOUND_LEG_NUMBER_OF_NIGHTS_SELECTOR_REF,
                                    containerRef: STOPOVER_REFS.STOPOVER_OUTBOUND_LEG_NUMBER_OF_NIGHTS_SELECTOR_REF,
                                    loading: loading,
                                }}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <StopoverLeg
                                IslandSelectProps={{
                                    label: t`ON RETURN`,
                                    placeholder: t`Select the island`,
                                    value: inbound.selectedAirport || '',
                                    disabledIslands: aInbound ? Object.entries(aInbound).reduce((a, [k, v]) => !v?.length ? [...a, k] : a, []) : [],
                                    onFocus: () => dispatch(setSwState({focusedComponent: COMPONENTS.STOPOVER_INBOUND_LEG_ISLAND_SELECTOR_REF})),
                                    onBlur: () => focusedComponent === COMPONENTS.STOPOVER_INBOUND_LEG_ISLAND_SELECTOR_REF && dispatch(setSwState({focusedComponent: COMPONENTS.NONE})),
                                    onSelect: selectedAirport => {
                                        dispatch(setSwBookStopoverInboundState({selectedAirport}));
                                        dispatch(setSwState({focusedComponent: COMPONENTS.STOPOVER_INBOUND_LEG_NUMBER_OF_NIGHTS_SELECTOR_REF}));
                                    },
                                    disabled: inboundLegDisabled || !inbound.enabled,
                                    open: focusedComponent === COMPONENTS.STOPOVER_INBOUND_LEG_ISLAND_SELECTOR_REF,
                                    containerRef: STOPOVER_REFS.STOPOVER_INBOUND_LEG_ISLAND_SELECTOR_REF,
                                    loading: loading,
                                }}
                                SwitchProps={{
                                    checked: Boolean(inbound.enabled),
                                    onChange: (e, enabled) => dispatch(setSwBookStopoverInboundState({enabled})),
                                    disabledAppearance: !inbound.enabled,
                                    disabled: inboundLegDisabled,
                                    ariaLabel: 'Select Departure Island',
                                }}
                                NightsSelectProps={{
                                    label: t`DURATION`,
                                    placeholder: t`Nights`,
                                    value: inbound?.numberOfNights ? (inbound.numberOfNights === 1 ? t`${inbound.numberOfNights} night` : t`${inbound.numberOfNights} nights`) : '',
                                    availableNights: inbound.selectedAirport ? aInbound[inbound.selectedAirport] : [],
                                    onFocus: () => dispatch(setSwState({focusedComponent: COMPONENTS.STOPOVER_INBOUND_LEG_NUMBER_OF_NIGHTS_SELECTOR_REF})),
                                    onBlur: () => focusedComponent === COMPONENTS.STOPOVER_INBOUND_LEG_NUMBER_OF_NIGHTS_SELECTOR_REF && dispatch(setSwState({focusedComponent: COMPONENTS.NONE})),
                                    onSelect: availableNight => {
                                        dispatch(setSwBookStopoverInboundState({numberOfNights: availableNight?.numberOfNights, departureDate: availableNight?.date}));
                                        dispatch(setSwState({focusedComponent: COMPONENTS.NONE}));
                                    },
                                    disabled: inboundLegDisabled || !inbound.enabled,
                                    open: focusedComponent === COMPONENTS.STOPOVER_INBOUND_LEG_NUMBER_OF_NIGHTS_SELECTOR_REF,
                                    containerRef: STOPOVER_REFS.STOPOVER_INBOUND_LEG_NUMBER_OF_NIGHTS_SELECTOR_REF,
                                    loading: loading,
                                }}
                            />
                        </Grid>
                        <Grid xs={12} item>
                            <PassengersCountSelectorConnected/>
                        </Grid>
                        <Box display={'flex'} flexDirection={isMobile ? 'column' : 'row'} justifyContent={'space-between'} alignItems={'center'} width={'100%'} p={2}>
                            <Box width={!isMobile ? 'calc(50% - 8px)' : '100%'}>
                                <BookStopoverSubmitButtonConnected/>
                            </Box>
                            <Box width={!isMobile ? 'calc(50% - 8px)' : '100%'} marginTop={isMobile ? '3px' : '0'}>
                                <BookStopoverWithExperiencesConnected/>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                {!isTablet && (
                    <Grid xs={12} md={12} lg={6} item>
                        <StopoverGridImages/>
                    </Grid>
                )}
                <BookStopoverAlertConnected/>
            </Grid>
        </Box>
    );
};

export default BookStopoverConnected;